/**
 * Agent Configuration API
 * All API calls for agent config management
 */

import apiClient from './client';
import type {
  AgentConfiguration,
  AgentConfigResponse,
  AgentListResponse,
  AgentConfigVersion,
} from '@/types';

/** Backend GET /{agent_type} returns config fields + metadata in a flat dict */
type AgentConfigRawResponse = AgentConfiguration & {
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
};

/** Backend PUT /{agent_type}/update returns a lightweight acknowledgement (not the full config) */
export interface AgentConfigUpdateResponse {
  agent_type: string;
  version: number;
  message: string;
}

export const agentApi = {
  /**
   * List all agent configurations
   * Endpoint: /agent-configs/agents/list
   */
  list: async (includeInactive = false): Promise<AgentListResponse> => {
    const { data } = await apiClient.get<AgentListResponse>('/agent-configs/agents/list', {
      params: { include_inactive: includeInactive },
    });
    return data;
  },

  /**
   * Get a specific agent configuration
   * Endpoint: /agent-configs/{agent_type}
   */
  get: async (agentType: string): Promise<AgentConfigRawResponse> => {
    const { data } = await apiClient.get<AgentConfigRawResponse>(`/agent-configs/${agentType}`);
    return data;
  },

  /**
   * Update agent configuration
   * Endpoint: /agent-configs/{agent_type}/update
   */
  update: async (
    agentType: string,
    config: Partial<AgentConfiguration>,
    description?: string,
    expectedVersion?: number,
  ): Promise<AgentConfigUpdateResponse> => {
    const { data } = await apiClient.put<AgentConfigUpdateResponse>(
      `/agent-configs/${agentType}/update`,
      config,
      {
        params: {
          ...(description && { description }),
          ...(expectedVersion !== undefined && { expected_version: expectedVersion }),
        },
      }
    );
    return data;
  },

  /**
   * Create new agent configuration
   * Endpoint: /agent-configs/{agent_type}/create
   */
  create: async (config: AgentConfiguration): Promise<AgentConfigResponse> => {
    const agentType = config.agent_type;
    const { data } = await apiClient.post<AgentConfigResponse>(
      `/agent-configs/${agentType}/create`,
      config
    );
    return data;
  },

  /**
   * Delete/deactivate agent configuration
   */
  delete: async (agentType: string): Promise<void> => {
    await apiClient.delete(`/agent-configs/${agentType}`);
  },

  /**
   * Reactivate deactivated agent
   */
  reactivate: async (agentType: string): Promise<AgentConfigResponse> => {
    const { data } = await apiClient.post<AgentConfigResponse>(
      `/agent-configs/${agentType}/reactivate`
    );
    return data;
  },

  /**
   * Get version history (paginated)
   * Endpoint: /agent-configs/{agent_type}/versions
   */
  getVersions: async (
    agentType: string,
    page = 1,
    pageSize = 20
  ): Promise<{ items: AgentConfigVersion[]; total: number; page: number; page_size: number; total_pages: number }> => {
    const { data } = await apiClient.get(
      `/agent-configs/${agentType}/versions`,
      { params: { page, page_size: pageSize } }
    );
    return data;
  },

  /**
   * Activate a specific version
   * Endpoint: /agent-configs/{agent_type}/activate/{version}
   */
  activateVersion: async (agentType: string, version: number, reason?: string): Promise<AgentConfigResponse> => {
    const { data } = await apiClient.post<AgentConfigResponse>(
      `/agent-configs/${agentType}/activate/${version}`,
      { reason }
    );
    return data;
  },

  /**
   * Invalidate agent cache
   * Endpoint: /agent-configs/cache/invalidate
   */
  invalidateCache: async (agentType?: string): Promise<void> => {
    await apiClient.post('/agent-configs/cache/invalidate', {
      agent_type: agentType,
      scope: agentType ? 'agent' : 'all',
    });
  },

  /**
   * Export agent configuration(s)
   * Endpoint: /agent-configs/export
   */
  export: async (agentTypes?: string[]): Promise<Blob> => {
    const { data } = await apiClient.get('/agent-configs/export', {
      params: { agent_types: agentTypes?.join(',') },
      responseType: 'blob',
    });
    return data;
  },

  /**
   * Import agent configuration(s)
   * Endpoint: /agent-configs/import
   */
  import: async (
    file: File,
    mode: 'create-only' | 'overwrite' | 'merge' = 'create-only',
    skipErrors = true
  ): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);
    formData.append('skip_errors', String(skipErrors));

    const { data } = await apiClient.post('/agent-configs/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};

export default agentApi;
